import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Phone, Mail, Globe, MapPin, Linkedin, Facebook, Instagram, Youtube, Music2 } from "lucide-react";
import { api } from "@/lib/api";

const RZ_LOGO = "https://customer-assets.emergentagent.com/job_safety-courses-3/artifacts/hmocjdk7_logo%20RZ.png";
const SM_LOGO = "/photos/semplicemente-logo.png";

const DEFAULTS = {
  phone: "0445 043370", email: "formazione@rischio-zero.it", website: "https://rischio-zero.it",
  address: "Via Prà Bordoni, 46 - 36010 Zanè (VI)", maps_query: "Via Prà Bordoni 46 Zanè VI",
  instagram: "", facebook: "", linkedin: "", tiktok: "", youtube: "",
};

const SOCIALS = [
  { key: "instagram", Icon: Instagram },
  { key: "facebook", Icon: Facebook },
  { key: "linkedin", Icon: Linkedin },
  { key: "youtube", Icon: Youtube },
  { key: "tiktok", Icon: Music2 },
];

const cleanUrl = (u) => (u ? u.replace(/^https?:\/\//, "").replace(/\/$/, "") : "");

export const Footer = () => {
  const [s, setS] = React.useState(DEFAULTS);
  React.useEffect(() => { api.get("/settings").then((r) => setS({ ...DEFAULTS, ...r.data })).catch(() => {}); }, []);

  const telHref = `tel:${(s.phone || "").replace(/\s/g, "")}`;
  const mapSrc = `https://www.google.com/maps?q=${encodeURIComponent(s.maps_query || s.address || "")}&output=embed`;
  const activeSocials = SOCIALS.filter((x) => s[x.key]);

  return (
    <footer id="contatti" className="bg-[#F9FAFB] border-t border-gray-100 mt-16 sm:mt-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-16">
          <div>
            <img src={RZ_LOGO} alt="Rischio Zero Health & Safety" className="h-14 object-contain -ml-1" data-testid="footer-rz-logo" />
            <p className="text-sm text-gray-500 leading-relaxed max-w-xs mt-4">
              {s.footer_about || "Formazione in materia di Salute e Sicurezza sul Lavoro. Corsi obbligatori, aggiornamenti e consulenza per la tua azienda."}
            </p>
            <Link to="/semplicemente" data-testid="footer-sm-link" className="mt-5 inline-flex items-center gap-2 rounded-2xl bg-white border border-gray-200 px-3 py-2 hover:border-[#8B5CF6]/40 transition-colors">
              <img src={SM_LOGO} alt="SempliceMente" className="h-6 object-contain" />
              <span className="text-xs font-600 text-gray-500">Scopri il progetto</span>
            </Link>
            {activeSocials.length > 0 && (
              <div className="flex items-center gap-3 mt-6" data-testid="footer-socials">
                {activeSocials.map(({ key, Icon }) => (
                  <a key={key} href={s[key]} target="_blank" rel="noopener noreferrer" data-testid={`social-link-${key}`}
                    aria-label={key}
                    className="w-9 h-9 flex items-center justify-center rounded-full bg-white border border-gray-200 text-gray-500 hover:text-[#EA6A0A] hover:border-[#EA6A0A]/30 transition-colors">
                    <Icon size={16} />
                  </a>
                ))}
              </div>
            )}
          </div>

          <div>
            <h4 className="font-display font-700 text-sm uppercase tracking-wide text-gray-400 mb-5">Contatti</h4>
            <ul className="space-y-4 text-sm">
              {s.phone && (
                <li>
                  <a href={telHref} data-testid="footer-phone" className="flex items-center gap-3 text-gray-600 hover:text-[#EA6A0A] transition-colors">
                    <span className="w-9 h-9 flex items-center justify-center rounded-full bg-[#EA6A0A]/5 text-[#EA6A0A]"><Phone size={16} /></span>{s.phone}
                  </a>
                </li>
              )}
              {s.email && (
                <li>
                  <a href={`mailto:${s.email}`} data-testid="footer-email" className="flex items-center gap-3 text-gray-600 hover:text-[#EA6A0A] transition-colors break-all">
                    <span className="w-9 h-9 flex items-center justify-center rounded-full bg-[#EA6A0A]/5 text-[#EA6A0A] shrink-0"><Mail size={16} /></span>{s.email}
                  </a>
                </li>
              )}
              {s.website && (
                <li>
                  <a href={s.website} target="_blank" rel="noopener noreferrer" data-testid="footer-website" className="flex items-center gap-3 text-gray-600 hover:text-[#EA6A0A] transition-colors">
                    <span className="w-9 h-9 flex items-center justify-center rounded-full bg-[#EA6A0A]/5 text-[#EA6A0A]"><Globe size={16} /></span>{cleanUrl(s.website)}
                  </a>
                </li>
              )}
              {s.address && (
                <li className="flex items-start gap-3 text-gray-600">
                  <span className="w-9 h-9 flex items-center justify-center rounded-full bg-[#EA6A0A]/5 text-[#EA6A0A] shrink-0"><MapPin size={16} /></span>
                  <span className="pt-2">{s.address}</span>
                </li>
              )}
            </ul>
          </div>

          <div>
            <h4 className="font-display font-700 text-sm uppercase tracking-wide text-gray-400 mb-5">Dove siamo</h4>
            <div className="rounded-2xl overflow-hidden border border-gray-200 shadow-sm">
              <iframe title="Mappa Rischio Zero" data-testid="google-map" src={mapSrc} width="100%" height="220" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
            </div>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-400">
          <span>© {new Date().getFullYear()} Rischio Zero — Tutti i diritti riservati.</span>
          <div className="flex items-center gap-3">
            <span>Calendario Corsi · 3ª Sessione 2026</span>
            <span className="text-gray-300">·</span>
            <Link to="/admin/login" data-testid="footer-admin-link" className="hover:text-[#EA6A0A] transition-colors">Area riservata</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
